import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'
import { Button, Flex, List, Modal, ModalProps, Table } from 'antd'
import { ColumnType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useState } from 'react'

import { useInvestigationsServiceGetInvestigationTestsEndpoint } from 'src/api/queries'
import { InvestigationTestsListItemDto } from 'src/api/requests'
import { ResultModal, TestStatusTag, Text } from 'src/components'
import { getFullName } from 'src/features/users/utils'
import { useTablePagination } from 'src/hooks'
import { dayMonthYearHourMinutesFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'

import { useStyles } from '../styles'

type Props = {
  specimenIds: string[]
  onCancel: () => void
} & ModalProps

export const InvestigationTestModal = ({
  onCancel,
  specimenIds,
  ...props
}: Props) => {
  const { t, currentLang } = useLocales()
  const { styles } = useStyles()
  const pagination = useTablePagination()

  const [currentTestId, setCurrentTestId] = useState<string>()

  const { data, isLoading: isLoadingTests } =
    useInvestigationsServiceGetInvestigationTestsEndpoint(
      {
        specimenIds
      },
      [specimenIds],
      { enabled: !!specimenIds }
    )

  const onCloseModal = () => {
    setCurrentTestId(undefined)
  }

  const columns: ColumnType<InvestigationTestsListItemDto>[] = [
    {
      key: 'code',
      title: t('investigations.test_number'),
      dataIndex: 'code'
    },
    {
      key: 'creation date',
      title: t('consultations.harvest_date'),
      render: (_, { testingAt }) =>
        testingAt
          ? dayjs
              .utc(testingAt)
              .tz()
              .format(dayMonthYearHourMinutesFormatWithDot)
          : '-'
    },
    {
      key: 'result',
      title: t('analysis.result'),
      render: (_, { results }) => {
        if (!results || results.length === 0) return '-'

        return (
          <Flex vertical gap={8}>
            <List
              dataSource={results}
              renderItem={(item) => {
                const testType =
                  item.testType?.translation?.[currentLang.value] || '-'
                const resultOption =
                  item?.resultOption?.translation?.[currentLang?.value] || '-'
                const resultValue = item?.resultValue ?? '-'
                const resultValueOption =
                  item?.resultValueOption?.translation?.[currentLang?.value] ||
                  '-'
                const resultDate = item?.resultDate
                  ? dayjs
                      .utc(item.resultDate)
                      .tz()
                      .format(dayMonthYearHourMinutesFormatWithDot)
                  : '-'

                return (
                  <List.Item>
                    <Flex vertical gap={8}>
                      <Text weight={400}>
                        {testType}, {resultOption}, {resultValue},{' '}
                        {resultValueOption}, {resultDate}
                      </Text>

                      <Text type="secondary" size="sm">
                        {t('tests_results.validated_by')}:{' '}
                        {item?.validatedByUser?.firstName
                          ? `${item?.validatedByUser?.firstName} ${item?.validatedByUser?.lastName}`
                          : '-'}
                      </Text>
                      <Text type="secondary" size="sm">
                        {t('tests_results.created_by')}:{' '}
                        {item?.createdByUser?.firstName
                          ? `${item?.createdByUser?.firstName} ${item?.createdByUser?.lastName}`
                          : '-'}
                      </Text>
                    </Flex>
                  </List.Item>
                )
              }}
            />
          </Flex>
        )
      }
    },
    {
      key: 'status',
      title: t('form.status'),
      render: (_, { status }) => <TestStatusTag status={status} />
    },
    {
      key: 'referral doctor',
      title: t('investigations.referral_doctor'),
      render: (_, { referralDoctorName, referralDoctorUser }) =>
        referralDoctorName ||
        getFullName(
          referralDoctorUser?.firstName,
          referralDoctorUser?.lastName
        ) ||
        '-'
    },
    {
      key: 'approved',
      title: t('investigations.approved'),
      render: (_, { isApproved }) => (
        <Flex justify="center">
          {isApproved ? (
            <CheckCircleOutlined className={styles.successIcon} />
          ) : (
            <CloseCircleOutlined className={styles.errorIcon} />
          )}
        </Flex>
      )
    },
    {
      key: 'details',
      render: (_, { id }) => (
        <Button
          type="link"
          onClick={() => {
            setCurrentTestId(id)
          }}
        >
          {t('common.details')}
        </Button>
      )
    }
  ]

  return (
    <Modal
      open
      width={1200}
      maskClosable={false}
      onCancel={onCancel}
      footer={null}
      title={t('investigations.tests')}
      styles={{
        body: {
          maxHeight: '70vh',
          overflowY: 'auto'
        }
      }}
      {...props}
    >
      <Table
        rowKey="id"
        loading={isLoadingTests}
        bordered
        dataSource={data?.data}
        columns={columns}
        scroll={{ x: 'max-content' }}
        pagination={{ ...pagination, total: data?.totalCount }}
      />

      {currentTestId && (
        <ResultModal testId={currentTestId} onCancel={onCloseModal} />
      )}
    </Modal>
  )
}
